import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  selectFilters,
  setFilters,
  setSelectedSkuBaseId,
  clearSelectedSkuBaseId,
} from "@/entities/gondola-change-groups/model/slice";
import {
  ChangeGroupsFilterParams,
  SKUBaseId,
} from "@/entities/gondola-change-groups/model/types";

import { FilterOption, FILTER_OPTIONS } from "../lib/const";
import { useUrlSyncedState } from "@/features/assignment/hooks/useUrlSyncedState";
import { useGondolaChangeGroupsData } from "./useGondolaChangeGroupsData";
import { combineCartLinesWithMetrics } from "../lib/helpers";

export interface UseGondolaChangeGroupsFiltersResult {
  filters: ChangeGroupsFilterParams;
  setSelectedProduct: (skuBaseId: SKUBaseId | null) => void;
  clearSelectedProduct: () => void;

  setAcceptedSkuSectionGroups: (option: FilterOption, value?: number) => void;
  setIntersectedAnnotations: (option: FilterOption, value?: number) => void;
  setIntersectedProxs: (option: FilterOption, value?: number) => void;
  updateFilters: (updates: Partial<ChangeGroupsFilterParams>) => void;
}

const URL_PARAMS = {
  SKU: "sku",
  STATUS: "status",
  STATUS_VALUE: "statusValue",
  ANNOTATIONS: "annotations",
  ANNOTATIONS_VALUE: "annotationsValue",
  PROXIMITY: "proximity",
  PROXIMITY_VALUE: "proximityValue",
} as const;

type FilterConfig = { option: FilterOption; value?: number };

const isValidFilterOption = (option: string): option is FilterOption => {
  return Object.values(FILTER_OPTIONS).includes(option as FilterOption);
};

const parseNumericValue = (value: string | null): number | undefined => {
  if (!value?.trim()) return undefined;
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

const serializeFilter = (
  filter: FilterConfig | null | undefined
): string | null => {
  return filter?.option !== FILTER_OPTIONS.ALL
    ? (filter?.option ?? null)
    : null;
};

const serializeFilterValue = (
  filter: FilterConfig | null | undefined
): string | null => {
  return filter?.value !== undefined && filter?.option !== FILTER_OPTIONS.ALL
    ? String(filter.value)
    : null;
};

const deserializeFilter = (
  option: string | null,
  value: string | null
): FilterConfig => {
  if (!option || !isValidFilterOption(option)) {
    return { option: FILTER_OPTIONS.ALL };
  }
  return {
    option,
    value: parseNumericValue(value),
  };
};

const processFilterParams = (urlParams: Record<string, string | null>) => {
  const filters: Partial<ChangeGroupsFilterParams> = {};

  if (urlParams[URL_PARAMS.SKU]) {
    filters.selectedSkuBaseId = urlParams[URL_PARAMS.SKU];
  }

  if (urlParams[URL_PARAMS.STATUS]) {
    filters.acceptedSkuSectionGroups = deserializeFilter(
      urlParams[URL_PARAMS.STATUS],
      urlParams[URL_PARAMS.STATUS_VALUE]
    );
  }

  if (urlParams[URL_PARAMS.ANNOTATIONS]) {
    filters.intersectedAnnotations = deserializeFilter(
      urlParams[URL_PARAMS.ANNOTATIONS],
      urlParams[URL_PARAMS.ANNOTATIONS_VALUE]
    );
  }

  if (urlParams[URL_PARAMS.PROXIMITY]) {
    filters.intersectedProxs = deserializeFilter(
      urlParams[URL_PARAMS.PROXIMITY],
      urlParams[URL_PARAMS.PROXIMITY_VALUE]
    );
  }

  return filters;
};

export const useGondolaChangeGroupsFilters = (options?: {
  preservedSearchParams?: string[];
}): UseGondolaChangeGroupsFiltersResult => {
  const { preservedSearchParams } = options || {};

  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);
  const { getParams, updateParams, searchParams } = useUrlSyncedState();
  const { data } = useGondolaChangeGroupsData();
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;

    const urlParams = getParams(Object.values(URL_PARAMS));
    const urlFilters = processFilterParams(urlParams);

    if (Object.keys(urlFilters).length > 0) {
      dispatch(setFilters(urlFilters));
    }

    isInitialized.current = true;
  }, [getParams]);

  useEffect(() => {
    if (!data || !filters.selectedSkuBaseId) return;

    const cartLines = combineCartLinesWithMetrics(data);
    const skuExists = cartLines.some(
      (line) => line.sku_base_id === filters.selectedSkuBaseId
    );

    if (!skuExists) {
      dispatch(clearSelectedSkuBaseId());
    }
  }, [data, filters.selectedSkuBaseId, dispatch]);

  const urlParams = useMemo(() => {
    if (!isInitialized.current) return {};

    const params: Record<string, string | null> = {
      [URL_PARAMS.SKU]: filters.selectedSkuBaseId
        ? String(filters.selectedSkuBaseId)
        : null,
      [URL_PARAMS.STATUS]: serializeFilter(filters.acceptedSkuSectionGroups),
      [URL_PARAMS.STATUS_VALUE]: serializeFilterValue(
        filters.acceptedSkuSectionGroups
      ),
      [URL_PARAMS.ANNOTATIONS]: serializeFilter(filters.intersectedAnnotations),
      [URL_PARAMS.ANNOTATIONS_VALUE]: serializeFilterValue(
        filters.intersectedAnnotations
      ),
      [URL_PARAMS.PROXIMITY]: serializeFilter(filters.intersectedProxs),
      [URL_PARAMS.PROXIMITY_VALUE]: serializeFilterValue(
        filters.intersectedProxs
      ),
    };

    const knownParams = Object.values(URL_PARAMS) as string[];
    for (const param of searchParams.keys()) {
      if (
        !knownParams.includes(param) &&
        !preservedSearchParams?.includes(param)
      ) {
        params[param] = null;
      }
    }

    return params;
  }, [filters, searchParams]);

  useEffect(() => {
    if (!isInitialized.current) return;
    updateParams(urlParams);
  }, [urlParams, updateParams]);

  const setSelectedProduct = useCallback(
    (skuBaseId: SKUBaseId | null) => dispatch(setSelectedSkuBaseId(skuBaseId)),
    [dispatch]
  );

  const clearSelectedProduct = useCallback(
    () => dispatch(clearSelectedSkuBaseId()),
    [dispatch]
  );

  const updateFilters = useCallback(
    (updates: Partial<ChangeGroupsFilterParams>) =>
      dispatch(setFilters(updates)),
    [dispatch]
  );

  const setAcceptedSkuSectionGroups = useCallback(
    (option: FilterOption, value?: number) =>
      dispatch(setFilters({ acceptedSkuSectionGroups: { option, value } })),
    [dispatch]
  );

  const setIntersectedAnnotations = useCallback(
    (option: FilterOption, value?: number) =>
      dispatch(setFilters({ intersectedAnnotations: { option, value } })),
    [dispatch]
  );

  const setIntersectedProxs = useCallback(
    (option: FilterOption, value?: number) =>
      dispatch(setFilters({ intersectedProxs: { option, value } })),
    [dispatch]
  );

  return {
    filters,
    setSelectedProduct,
    clearSelectedProduct,
    setAcceptedSkuSectionGroups,
    setIntersectedAnnotations,
    setIntersectedProxs,
    updateFilters,
  };
};
